import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { DefinitionMatchContent, DefinitionPair } from "../content-types";
import { Explanation, Markup, Prompt } from "./shared";
import { GlossableText, isLanguageFramework } from "./glossable-text";
import type { QuestionRendererProps } from "./types";

/** Match each term to its definition. The learner picks a definition per term
 *  from a shuffled list. Graded when every term maps to its own definition. */
export function DefinitionMatchRenderer({
  content,
  answer,
  setAnswer,
  revealed,
  frameworkSlug,
  translateAnswers,
}: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as DefinitionMatchContent;
  const enableGloss = isLanguageFramework(frameworkSlug);
  const enableAnswerGloss = enableGloss && !!translateAnswers;
  const pairs: DefinitionPair[] = c.pairs ?? [];
  const map = (answer as Record<string, string>) ?? {};

  // Shuffle the definition choices once.
  const choices = useMemo(() => {
    const defs = pairs.map((p) => p.definition);
    for (let i = defs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [defs[i], defs[j]] = [defs[j], defs[i]];
    }
    return defs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFor = (termId: string, def: string) => setAnswer({ ...map, [termId]: def });

  return (
    <div>
      <Prompt>{c.instruction || t("Match each term to its definition")}</Prompt>
      <div className="mt-4 space-y-3">
        {pairs.map((p) => {
          const chosen = map[p.term_id] ?? "";
          const ok = revealed ? chosen === p.definition : null;
          return (
            <div key={p.term_id}>
              <div className="flex items-center gap-3">
                <div className="w-2/5 shrink-0 text-sm font-semibold text-ink">
                  {enableAnswerGloss ? (
                    <GlossableText text={p.term} context={c.instruction} />
                  ) : (
                    <Markup>{p.term}</Markup>
                  )}
                </div>
                <select
                  value={chosen}
                  disabled={revealed}
                  onChange={(e) => setFor(p.term_id, e.target.value)}
                  className={`flex-1 rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                    revealed
                      ? ok
                        ? "border-emerald-400 bg-emerald-50"
                        : "border-red-400 bg-red-50"
                      : "border-black/10 bg-white"
                  }`}
                >
                  <option value="">{t("Choose…")}</option>
                  {choices.map((d, i) => (
                    <option key={i} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Correct:")}{" "}
                  <span className="font-semibold">
                    {enableAnswerGloss ? (
                      <GlossableText text={p.definition} context={c.instruction} />
                    ) : (
                      <Markup>{p.definition}</Markup>
                    )}
                  </span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
